import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { incrementListingViews } from '@/services/api/listings'
import type { Listing } from '@/types/listing'

interface UseListingReturn {
  listing: Listing | null
  isLoading: boolean
  error: string | null
}

// Hook to fetch a single listing with real-time updates
export function useListing(id: string | undefined): UseListingReturn {
  const [listing, setListing] = useState<Listing | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) {
      setIsLoading(false)
      setListing(null)
      return
    }

    setIsLoading(true)
    setError(null)

    const docRef = doc(db, 'listings', id)

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setListing({ id: docSnap.id, ...docSnap.data() } as Listing)
        } else {
          setListing(null)
          setError('Listing not found')
        }
        setIsLoading(false)
      },
      (err) => {
        console.error('Error fetching listing:', err)
        setError(err.message)
        setIsLoading(false)
      }
    )

    // Increment view count (fire and forget)
    incrementListingViews(id).catch(console.error)

    return () => unsubscribe()
  }, [id])

  return { listing, isLoading, error }
}
